import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToggler } from '@cbhq/cds-common/hooks/useToggler';
import { HStack, VStack } from '@cbhq/cds-web/layout';

import useLocalStorage from ':cloud/hooks/useLocalStorage';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { EventName, logClick } from ':cloud/init/clientAnalytics/logging';
import { useGetBuildQuestQuestions } from ':cloud/queries/BuildQuestQueries/useGetBuildQuestQuestions';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { useBuildQuestContext } from ':cloud/widgets/buildQuest/BuildQuestProvider';
import { CenterTile } from ':cloud/widgets/buildQuest/CenterTile';
import { confettiRain } from ':cloud/widgets/buildQuest/confetti';
import { TILE_HEIGHT } from ':cloud/widgets/buildQuest/constants';
import { GameTile } from ':cloud/widgets/buildQuest/GameTile';
import { LevelUpModal } from ':cloud/widgets/buildQuest/LevelUpModal';
import { PlayerPanel } from ':cloud/widgets/buildQuest/PlayerPanel';
import { QuestionModal } from ':cloud/widgets/buildQuest/QuestionModal';
import { SimpleZoomable } from ':cloud/widgets/buildQuest/SimpleZoomable';
import { WelcomeModal } from ':cloud/widgets/buildQuest/WelcomeModal';

const padding = 100;
const mainWrapperPadding = { paddingTop: padding };
function getRowOffset(row: number) {
  return row * (TILE_HEIGHT - 48);
}
const totalRows = 4;

export function BuildQuestGameBoard(): React.ReactElement {
  const { isPhone } = useSimpleBreakpoints();
  const { user } = useGetUser();
  const { items } = useGetBuildQuestQuestions(user.userId);
  const { totalPointsAwarded, setDisplayedPoints } = useBuildQuestContext();

  const [selectedQuestionId, setSelectedQuestionId] = useState<string>('');

  const [
    isQuestionModalVisible,
    { toggleOn: toggleQuestionModalOn, toggleOff: toggleQuestionModalOff },
  ] = useToggler();
  const [
    isLevelUpModalVisible,
    { toggleOn: toggleLevelUpModalOn, toggleOff: toggleLevelUpModalOff },
  ] = useToggler();

  const [hasViewedWelcomeModal, setHasViewedWelcomeModal] = useLocalStorage(
    `viewed-quest-welcome-modal-${user.userId}`,
    false,
  );
  const [
    isWelcomeModalVisible,
    { toggleOn: toggleWelcomeModalOn, toggleOff: toggleWelcomeModalOff },
  ] = useToggler();
  useEffect(() => {
    if (!hasViewedWelcomeModal) {
      toggleWelcomeModalOn();
    }
  }, [hasViewedWelcomeModal, toggleWelcomeModalOn]);
  const handleCloseWelcomeModal = useCallback(() => {
    setHasViewedWelcomeModal(true);
    toggleWelcomeModalOff();
  }, [setHasViewedWelcomeModal, toggleWelcomeModalOff]);

  const completedItems = useMemo(() => items.filter((item) => item.complete), [items]);
  const tierItems = useMemo(
    () => ({
      beginner: ['1', '2'],
      backend: ['3', '4'],
      frontend: ['5', '6'],
      final: ['7'],
    }),
    [],
  );

  const isTierComplete = useCallback(
    (tier: keyof typeof tierItems) => {
      const itemIdsInTier = tierItems[tier];
      const tierItemsCompleted = completedItems.filter((item) =>
        itemIdsInTier.includes(item.questionId),
      );
      return tierItemsCompleted.length >= itemIdsInTier.length;
    },
    [completedItems, tierItems],
  );

  const [completedTiers, setCompletedTiers] = useState(() => ({
    beginner: isTierComplete('beginner'),
    backend: isTierComplete('backend'),
    frontend: isTierComplete('frontend'),
    final: isTierComplete('final'),
  }));

  const handleLevelUpCheck = useCallback(() => {
    const newCompletedTiers = {
      beginner: isTierComplete('beginner'),
      backend: isTierComplete('backend'),
      frontend: isTierComplete('frontend'),
      final: isTierComplete('final'),
    };

    if (
      newCompletedTiers.beginner !== completedTiers.beginner ||
      newCompletedTiers.backend !== completedTiers.backend ||
      newCompletedTiers.frontend !== completedTiers.frontend ||
      newCompletedTiers.final !== completedTiers.final
    ) {
      setCompletedTiers(newCompletedTiers);
      toggleLevelUpModalOn();
    }
  }, [completedTiers, isTierComplete, toggleLevelUpModalOn]);

  const handleCloseQuestionModal = useCallback(() => {
    toggleQuestionModalOff();
    setSelectedQuestionId('');
  }, [toggleQuestionModalOff, setSelectedQuestionId]);

  // If you don't do this outside of the onRequestClose callback, state will not update
  // properly when the question modal is closed via setTimeout.
  const handleOnDidQuestionModalClose = useCallback(() => {
    setDisplayedPoints(totalPointsAwarded);
    handleLevelUpCheck();
  }, [totalPointsAwarded, setDisplayedPoints, handleLevelUpCheck]);

  const handleTilePress = useCallback(
    (tileId) => {
      setSelectedQuestionId(tileId);
      toggleQuestionModalOn();
      logClick(EventName.quest_question_click, { questionId: tileId });
    },
    [setSelectedQuestionId, toggleQuestionModalOn],
  );

  return (
    <>
      <VStack
        background="background"
        alignItems="center"
        width="100%"
        minHeight="100vh"
        style={mainWrapperPadding}
        overflow="hidden"
        id="confetti-wrapper"
        className={confettiRain}
      >
        <SimpleZoomable minScale={isPhone ? 0.8 : 0.5} maxScale={4} initialScale={1}>
          <VStack
            width="100%"
            alignItems="center"
            justifyContent="center"
            position="relative"
            minHeight={TILE_HEIGHT * totalRows - padding}
          >
            <HStack gap={2} position="absolute" top={getRowOffset(0)}>
              <GameTile questionId="1" isUnlocked variant="beginner" onPress={handleTilePress} />
              <GameTile questionId="2" isUnlocked variant="beginner" onPress={handleTilePress} />
            </HStack>
            {/* this is the center row */}
            <HStack gap={2} position="absolute" top={getRowOffset(1)}>
              <GameTile
                questionId="3"
                isUnlocked={completedTiers.beginner}
                variant="backend"
                onPress={handleTilePress}
              />
              <CenterTile completed /> {/* This one will always be complete if user is logged in */}
              <GameTile
                questionId="5"
                isUnlocked={completedTiers.beginner}
                variant="frontend"
                onPress={handleTilePress}
              />
            </HStack>
            <HStack gap={2} position="absolute" top={getRowOffset(2)}>
              <GameTile
                questionId="4"
                isUnlocked={completedTiers.beginner}
                variant="backend"
                onPress={handleTilePress}
              />
              <GameTile
                questionId="6"
                isUnlocked={completedTiers.beginner}
                variant="frontend"
                onPress={handleTilePress}
              />
            </HStack>
            <HStack gap={2} position="absolute" top={getRowOffset(3)}>
              <GameTile
                questionId="7"
                isUnlocked={completedTiers.backend || completedTiers.frontend}
                variant="final"
                onPress={handleTilePress}
              />
            </HStack>
          </VStack>
        </SimpleZoomable>
      </VStack>
      <PlayerPanel />
      <QuestionModal
        visible={isQuestionModalVisible}
        onRequestClose={handleCloseQuestionModal}
        onDidClose={handleOnDidQuestionModalClose}
        questionId={selectedQuestionId}
      />
      <LevelUpModal visible={isLevelUpModalVisible} onRequestClose={toggleLevelUpModalOff} />
      <WelcomeModal visible={isWelcomeModalVisible} onRequestClose={handleCloseWelcomeModal} />
    </>
  );
}
